/**
 * Follow-ups — the state of a row's Follow-up cell (redesign Phase 0, 2026-10-02).
 *
 * Plan: docs/plans/2026-10-02-followups-redesign-plan.md
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * ── SOURCE OF TRUTH: TODAY'S TEMPLATE, NOT THE PROTOTYPE ──────────────────────────────────────
 * Written from AdminTable.vue's Follow-up cell (the `v-if / v-else-if` chain in the
 * FollowUpActionPlan slot), in the SAME ORDER of checks, with the same strict/loose comparisons.
 * The design export's mock `fuState()` is simpler and disagrees with it in several places (a
 * stopped row whose date is today, a null date with attempts but FollowupStopped null, Not Set
 * for a read-only user, …) — where they differ, the legacy table wins, because it is what offices
 * use today. tests/component/FollowupsStateAgreement.spec.js mounts the REAL AdminTable.vue over a
 * matrix of rows and fails if the two ever disagree.
 *
 * The legacy chain, branch by branch (`legacyBranch` below names which one rendered):
 *   1. due-dropdown   isDueToday(FollowupDate) && can edit        → "Due Today" dropdown
 *   2. due-badge      isDueToday(FollowupDate)                    → "Due Today" badge
 *   3. stopped        TeamioFollowupStatusID === 3 || FollowupStopped === 1  → "Do Not Follow-up"
 *   4. done           FollowupDate === null && Attempts > 0 && FollowupStopped === 0 → "Follow-ups done"
 *   5. notset         FollowupDate === null && Status === 'Not Called'      → "Not Set" dropdown
 *                     ⚠ NO permission check — a read-only user can still set the first date.
 *   6. date-readonly  Status not 'Appointment Scheduled'/'Claim Received' && cannot edit → disabled date
 *   7. date-dropdown  Status not 'Appointment Scheduled'/'Claim Received' && can edit    → date dropdown
 *   8. none           anything else — the cell draws NOTHING (Status 'Appointment Scheduled' or
 *                     'Claim Received' that is not due, stopped, done or not-set). State 'resolved'.
 *
 * Branches 6/7 become 'past' or 'upcoming' by comparing the date with today, or 'nodate' when the
 * date is null/blank/unreadable — legacy then draws the date button with an EMPTY label (e.g. a
 * Status null row on the Admin dashboard, or a null date with attempts whose FollowupStopped is
 * null). 'nodate' and 'resolved' are the two states beyond the design's seven; both documented.
 *
 * ── "DUE TODAY" — THE SAME COMPARISON AS mixin.js isDueToday ──────────────────────────────────
 * Legacy: `d === null || d === '' ? false : moment(d).isSame(moment(), 'day')`, in moment's default
 * zone. Rows carry DATE columns as 'YYYY-MM-DD' strings (the layer's mysql config sets
 * dateStrings: true) and every save writes 'YYYY-MM-DD', so comparing the leading 'YYYY-MM-DD'
 * against `today` (computed by the caller in the same zone) is the same answer. Kept quirk:
 * `undefined` is NOT null and NOT '' — moment(undefined) is "now", so legacy calls a row with no
 * FollowupDate key at all "Due Today". Mirrored on purpose; pinned by the tests.
 */

/** Sort order of the Follow-up column (the design's, with the two legacy-only states slotted in). */
export const FOLLOWUP_STATE_ORDER = ['past', 'due', 'upcoming', 'nodate', 'notset', 'done', 'stopped', 'resolved', 'saving'];

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})(?:$|[ T])/;

/** 'YYYY-MM-DD' from a DATE/DATETIME string (or a Date, by its local fields); null when unreadable. */
export function toYmd(value) {
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    const p = n => (n < 10 ? `0${n}` : String(n));
    return `${value.getFullYear()}-${p(value.getMonth() + 1)}-${p(value.getDate())}`;
  }
  if (typeof value !== 'string') return null;
  const m = YMD_RE.exec(value);
  if (!m) return null;
  const mm = Number(m[2]);
  const dd = Number(m[3]);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
  return `${m[1]}-${m[2]}-${m[3]}`;
}

/** mixin.js isDueToday, against an injected `today` ('YYYY-MM-DD'). See the header for `undefined`. */
export function isDueToday(value, today) {
  if (value === null || value === '') return false;
  if (value === undefined) return true;
  const ymd = toYmd(value);
  return ymd !== null && ymd === today;
}

const RESOLVED_STATUSES = ['Appointment Scheduled', 'Claim Received'];

/**
 * The red "Remove Initial Set Follow-up" button replaces "Stop Follow-ups" inside the date
 * dropdown only for an untouched, automatically-created initial follow-up.
 */
function dangerActionOf(row) {
  return (row.Status === 'Not Called' || row.Status === null)
    && row.CreatedAutomatically === 1 && row.InitialFollowupDateSet === 1
    ? 'remove-initial'
    : 'stop';
}

/**
 * @param {Object}  row              a legacy queue row (OD + teamio.tasks column names)
 * @param {Object}  ctx
 * @param {string}  ctx.today        'YYYY-MM-DD' in the site's zone (moment().format('YYYY-MM-DD'))
 * @param {boolean} ctx.canEdit      $can('update', 'followupDate')
 * @param {boolean} [ctx.saving]     a save for this row is in flight
 * @returns {{
 *   state: 'saving'|'due'|'stopped'|'done'|'notset'|'past'|'upcoming'|'nodate'|'resolved',
 *   legacyBranch: string, editable: boolean, dangerAction: 'remove-initial'|'stop'|null,
 *   canRestart: boolean, pastDue: boolean, overridden: boolean, hasInitialNote: boolean,
 *   initialNote: string|null, manuallyAssigned: boolean, assignedToEmpID: *, date: string|null }}
 */
export function resolveFollowupState(row, ctx) {
  const r = row || {};
  const c = ctx || {};
  const today = c.today;
  const canEdit = c.canEdit === true;

  // The icons beside the cell are v-show'n independently of the chain — a stopped row can be due
  // today AND show the restart icon. Same expressions as the template.
  const canRestart = (r.TeamioFollowupStatusID === 3 || r.FollowupStopped === 1)
    || (r.FollowupDate === null && r.Attempts > 0 && r.FollowupStopped === 0);
  const flags = {
    canRestart: !!canRestart,
    pastDue: r.pastDue === 'true',
    overridden: r.Overridden === 1,
    hasInitialNote: !!r.InitialFollowupNote,
    initialNote: r.InitialFollowupNote ? String(r.InitialFollowupNote) : null,
    manuallyAssigned: r.EmployeeManuallyAssigned === 1,
    assignedToEmpID: r.AssignedToEmpID === undefined ? null : r.AssignedToEmpID,
    date: toYmd(r.FollowupDate),
  };
  const out = (state, legacyBranch, editable, dangerAction) =>
    Object.assign({ state, legacyBranch, editable, dangerAction }, flags);

  let result;
  if (isDueToday(r.FollowupDate, today) && canEdit) {
    result = out('due', 'due-dropdown', true, dangerActionOf(r));
  } else if (isDueToday(r.FollowupDate, today)) {
    result = out('due', 'due-badge', false, null);
  } else if (r.TeamioFollowupStatusID === 3 || r.FollowupStopped === 1) {
    result = out('stopped', 'stopped', false, null);
  } else if (r.FollowupDate === null && r.Attempts > 0 && r.FollowupStopped === 0) {
    result = out('done', 'done', false, null);
  } else if (r.FollowupDate === null && r.Status === 'Not Called') {
    result = out('notset', 'notset', true, null);
  } else if (RESOLVED_STATUSES.indexOf(r.Status) === -1) {
    let state = 'nodate';
    if (flags.date !== null) state = flags.date < today ? 'past' : 'upcoming';
    result = canEdit
      ? out(state, 'date-dropdown', true, dangerActionOf(r))
      : out(state, 'date-readonly', false, null);
  } else {
    result = out('resolved', 'none', false, null);
  }

  // The legacy spinner sits beside whatever the chain drew; the design replaces the cell with it.
  if (c.saving === true) {
    result.state = 'saving';
    result.editable = false;
    result.dangerAction = null;
  }
  return result;
}

/**
 * The Follow-up cell on PATIENT PROFILE (redesign Phase 5, 2026-10-02).
 *
 * Patient Profile's Follow-ups section is not AdminTable: it has its OWN copy of the chain
 * (PatientProfile.vue, the "DUE DATE" cell), and it differs. The redesigned section follows
 * Patient Profile's, branch by branch (tests/component/PatientProfileFollowupsAgreement.spec.js
 * renders the real section and fails on any difference):
 *   1. due-dropdown   isDueToday && can edit                 (same as AdminTable)
 *   2. due-badge      isDueToday                              (same)
 *   3. stopped        TeamioFollowupStatusID === 3 || FollowupStopped === 1   (same)
 *   4. done           FollowupDate === null && Status !== 'Not Called' && FollowupStopped === 0
 *                     ⚠ AdminTable tests `Attempts > 0` here, not the Status.
 *   —  there is NO "Not Set" branch: a null date with Status 'Not Called' falls through to 5/6.
 *   5. date-readonly  Status != 'Appointment Scheduled' && cannot edit
 *   6. date-dropdown  Status != 'Appointment Scheduled' && can edit
 *                     ⚠ only 'Appointment Scheduled' stops here — a 'Claim Received' row with a
 *                     date shows that date (AdminTable draws nothing for it).
 *   7. none           anything else → 'resolved'.
 * The flags (restart icon, past-due dot, overridden, note) are the same expressions in both
 * templates, so they come from resolveFollowupState unchanged.
 */
export function resolveProfileFollowupState(row, ctx) {
  const r = row || {};
  const c = ctx || {};
  const today = c.today;
  const canEdit = c.canEdit === true;
  const base = resolveFollowupState(r, { today, canEdit });
  const out = (state, legacyBranch, editable, dangerAction) =>
    Object.assign({}, base, { state, legacyBranch, editable, dangerAction });

  let result;
  if (isDueToday(r.FollowupDate, today) && canEdit) {
    result = out('due', 'due-dropdown', true, dangerActionOf(r));
  } else if (isDueToday(r.FollowupDate, today)) {
    result = out('due', 'due-badge', false, null);
  } else if (r.TeamioFollowupStatusID === 3 || r.FollowupStopped === 1) {
    result = out('stopped', 'stopped', false, null);
  } else if (r.FollowupDate === null && r.Status !== 'Not Called' && r.FollowupStopped === 0) {
    result = out('done', 'done', false, null);
  } else if (r.Status !== 'Appointment Scheduled') {
    let state = 'nodate';
    if (base.date !== null) state = base.date < today ? 'past' : 'upcoming';
    result = canEdit
      ? out(state, 'date-dropdown', true, dangerActionOf(r))
      : out(state, 'date-readonly', false, null);
  } else {
    result = out('resolved', 'none', false, null);
  }

  if (c.saving === true) {
    result.state = 'saving';
    result.editable = false;
    result.dangerAction = null;
  }
  return result;
}
